/**
 * API Configuration for Web and Electron
 * 
 * In web mode (Vercel/local): Uses relative URLs (same origin)
 * In Electron mode:
 *  - Dev server (http/https origin): uses relative URLs
 *  - Packaged app (file:// origin): uses production URL
 */

type DesktopRuntimeSignals = {
  /** `window.electronAPI` as exposed by the desktop shell's preload script. */
  electronAPI: { readonly isElectron?: unknown } | null | undefined;
  /** `NEXT_PUBLIC_ELECTRON`, set only by the desktop static-export build. */
  buildFlag: string | undefined;
};

/**
 * The desktop shell is identified by its own contract: the preload bridge it
 * injects into every app window, or the desktop-only build. The user agent is
 * not a signal: embedded browsers, preview tools, and the shell's own
 * preload-free windows report "Electron" without being the desktop app.
 * Classifying them as desktop moves their credential from the HttpOnly cookie
 * to a renderer session they can never hold, so every token expiry signed
 * them out without a refresh attempt.
 */
export function detectDesktopRuntime({ electronAPI, buildFlag }: DesktopRuntimeSignals): boolean {
  return buildFlag === 'true' || electronAPI?.isElectron === true;
}

export const isElectron = detectDesktopRuntime({
  electronAPI: typeof window === 'undefined' ? undefined : window.electronAPI,
  buildFlag: process.env.NEXT_PUBLIC_ELECTRON,
});

// Production API URL
// Use www.smartmanager.ai for packaged Electron (file:// origin)
const PRODUCTION_API_URL = 'https://www.smartmanager.ai';

// Canonical origin for links that must be usable outside the current browser,
// including notifications and cross-device continuity handoffs.
export const PUBLIC_APP_ORIGIN = PRODUCTION_API_URL;

// Optional explicit override for debugging/deployment environments.
const API_BASE_OVERRIDE = process.env.NEXT_PUBLIC_API_BASE_URL?.trim();

const hasHttpOrigin =
  typeof window !== 'undefined' &&
  (window.location.protocol === 'http:' || window.location.protocol === 'https:');

type ApiBaseUrlOptions = {
  override?: string;
  electron: boolean;
  httpOrigin: boolean;
  nodeEnv?: string;
};

/**
 * Electron development runs against the Next dev server, so relative API URLs
 * are correct there. Packaged builds instead render a static export from a
 * loopback HTTP server; those requests must target the hosted API explicitly.
 */
export function resolveApiBaseUrl({
  override,
  electron,
  httpOrigin,
  nodeEnv,
}: ApiBaseUrlOptions): string {
  const normalizedOverride = override?.trim();
  if (normalizedOverride) return normalizedOverride;
  if (!electron) return '';
  return nodeEnv === 'development' && httpOrigin ? '' : PRODUCTION_API_URL;
}

/**
 * Base URL for API calls
 * - Empty string for web (relative URLs work with same origin)
 * - Full production URL for Electron (cross-origin requests)
 */
export const API_BASE_URL = resolveApiBaseUrl({
  override: API_BASE_OVERRIDE,
  electron: isElectron,
  httpOrigin: hasHttpOrigin,
  nodeEnv: process.env.NODE_ENV,
});

/**
 * Helper function to build full API URL
 * Usage: apiUrl('/api/gptAnswer') => 'https://www.smartmanager.ai/api/gptAnswer' (Electron)
 *                                 => '/api/gptAnswer' (Web)
 */
export function apiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

/**
 * Builds an absolute same-origin app URL for links that leave the current
 * runtime. The optional origin supports local/dev callers without allowing the
 * path itself to replace the trusted origin.
 */
export function publicAppUrl(path: string, origin = PUBLIC_APP_ORIGIN): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath.startsWith('//') || normalizedPath.includes('\\')) {
    throw new Error('App URL path must be same-origin');
  }

  const parsedOrigin = new URL(origin);
  if (
    (parsedOrigin.protocol !== 'https:' && parsedOrigin.protocol !== 'http:') ||
    parsedOrigin.username ||
    parsedOrigin.password
  ) {
    throw new Error('App URL origin must use HTTP(S) without credentials');
  }

  return new URL(normalizedPath, `${parsedOrigin.origin}/`).toString();
}

/**
 * Enhanced fetch wrapper that automatically uses correct base URL
 * Drop-in replacement for fetch in API calls
 */
export async function apiFetch(
  path: string, 
  options?: RequestInit
): Promise<Response> {
  return fetch(apiUrl(path), {
    ...options,
    // Ensure credentials are included for cross-origin in Electron
    credentials: isElectron ? 'include' : (options?.credentials ?? 'same-origin'),
  });
}
